<template>
  <q-dialog :value="value" @input="$emit('input', $event)" persistent>
    <q-card style="min-width: 600px; max-width: 90vw">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          {{ form.id ? (isAdmin ? "Edit Review" : "Performance Review") : "New Performance Review" }}
        </div>
        <q-space />
        <q-btn icon="fas fa-times" flat round dense @click="close" />
      </q-card-section>

      <q-card-section>
        <q-form @submit="save" class="q-gutter-md">
          <q-select
            v-if="isAdmin"
            v-model="form.employee_id"
            label="Employee"
            :options="employeeOptions"
            option-value="id"
            :option-label="employeeLabel"
            emit-value
            map-options
            use-input
            outlined
            :disable="!!form.id"
            @filter="(val, update) => $emit('filter-employees', val, update)"
            :rules="[(val) => !!val || 'Employee is required']"
          />

          <div class="row q-col-gutter-sm">
            <q-input class="col-12 col-sm-6" v-model="form.review_period" label="Review Period (e.g. H1 2026)" outlined :disable="!canEditManager" :rules="[(val) => !!val || 'Required']" />
            <q-input class="col-12 col-sm-6" v-model="form.review_date" label="Review Date" outlined mask="####-##-##" placeholder="YYYY-MM-DD" :disable="!canEditManager" :rules="[(val) => !!val || 'Required']">
              <template v-slot:append>
                <i class="far fa-calendar-alt cursor-pointer" v-if="canEditManager">
                  <q-popup-proxy transition-show="scale" transition-hide="scale">
                    <q-date v-model="form.review_date" mask="YYYY-MM-DD" />
                  </q-popup-proxy>
                </i>
              </template>
            </q-input>
          </div>

          <div>
            <div class="text-subtitle2 q-mb-xs">Overall Rating</div>
            <q-rating v-model="form.overall_rating" max="5" size="2em" color="amber" :readonly="!canEditManager" />
          </div>

          <!-- Competencies -->
          <div>
            <div class="row items-center justify-between q-mb-sm">
              <div class="text-subtitle2">Competencies</div>
              <q-btn v-if="canEditManager" flat dense color="primary" icon="fas fa-plus" label="Add" @click="addCompetency" />
            </div>
            <div v-for="(comp, idx) in form.competencies" :key="idx" class="q-mb-sm q-pa-sm rounded-borders" style="border: 1px solid #e0e0e0">
              <div class="row q-col-gutter-sm items-center">
                <q-input class="col-12 col-sm-4" v-model="comp.competency_name" label="Competency" dense outlined :disable="!canEditManager" />
                <q-input class="col-6 col-sm-3" v-model.number="comp.target_rating" label="Target" type="number" min="0" max="5" dense outlined :disable="!canEditManager" />
                <q-input class="col-6 col-sm-3" v-model.number="comp.actual_rating" label="Actual" type="number" min="0" max="5" dense outlined :disable="!canEditManager" />
                <div class="col-12 col-sm-2 text-right">
                  <q-btn v-if="canEditManager" flat round dense color="red" icon="fas fa-trash-alt" @click="form.competencies.splice(idx, 1)" />
                </div>
                <q-input class="col-12" v-model="comp.comments" label="Comments" dense outlined :disable="!canEditManager" />
              </div>
            </div>
            <div v-if="form.competencies.length === 0" class="text-caption text-grey">No competencies added.</div>
          </div>

          <q-input v-model="form.manager_comments" label="Manager Comments" type="textarea" outlined :disable="!canEditManager" />
          <q-input v-model="form.employee_comments" label="Employee Comments" type="textarea" outlined :disable="!canEditEmployee" />

          <q-select
            v-if="canEditManager"
            v-model="form.status"
            label="Status"
            :options="statusOptions"
            outlined
          />

          <div class="row justify-end q-gutter-sm">
            <q-btn label="Close" color="grey" @click="close" />
            <q-btn
              v-if="canEditEmployee && !canEditManager && form.status !== 'acknowledged'"
              label="Acknowledge"
              color="teal"
              @click="acknowledge"
              :loading="saving"
            />
            <q-btn v-if="canEditManager || canEditEmployee" label="Save" type="submit" color="primary" :loading="saving" />
          </div>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import axios from "axios";
import mixins from "../../mixins";

export default {
  mixins: [mixins],
  props: {
    value: { type: Boolean, default: false },
    review: { type: Object, default: null },
    isAdmin: { type: Boolean, default: false },
    employeeOptions: { type: Array, default: () => [] },
    currentUserId: { type: [Number, String], default: null }
  },
  data() {
    return {
      saving: false,
      form: this.emptyForm(),
      statusOptions: ["draft", "under_review", "acknowledged", "completed"]
    };
  },
  computed: {
    // Managers/admins may edit the full review.
    canEditManager() {
      return this.isAdmin;
    },
    // The reviewed employee may add their own comments / acknowledge.
    canEditEmployee() {
      if (this.isAdmin) return false;
      return this.form.id && Number(this.form.employee_id) === Number(this.currentUserId);
    }
  },
  watch: {
    value(open) {
      if (open) this.hydrate();
    }
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        employee_id: null,
        review_period: "",
        overall_rating: 0,
        review_date: "",
        manager_comments: "",
        employee_comments: "",
        status: "draft",
        competencies: []
      };
    },
    hydrate() {
      if (this.review) {
        this.form = {
          id: this.review.id,
          employee_id: this.review.employee_id,
          review_period: this.review.review_period,
          overall_rating: this.review.overall_rating ? Number(this.review.overall_rating) : 0,
          review_date: this.review.review_date,
          manager_comments: this.review.manager_comments || "",
          employee_comments: this.review.employee_comments || "",
          status: this.review.status,
          competencies: (this.review.competencies || []).map((c) => ({
            competency_name: c.competency_name,
            target_rating: Number(c.target_rating),
            actual_rating: Number(c.actual_rating),
            comments: c.comments || ""
          }))
        };
      } else {
        this.form = this.emptyForm();
      }
    },
    employeeLabel(opt) {
      if (!opt) return "";
      return `${opt.firstname} ${opt.lastname}`;
    },
    addCompetency() {
      this.form.competencies.push({ competency_name: "", target_rating: 0, actual_rating: 0, comments: "" });
    },
    close() {
      this.$emit("input", false);
    },
    buildPayload() {
      const p = { ...this.form };
      // Normalize rating: q-rating uses 0-5 integer; overall_rating column is DECIMAL(3,2).
      p.overall_rating = this.form.overall_rating ? this.form.overall_rating : null;
      return p;
    },
    save() {
      const isEdit = !!this.form.id;
      const url = isEdit ? "/performance/reviews/update" : "/performance/reviews/submit";
      this.saving = true;
      axios
        .post(this.apiUrl + url, this.buildPayload(), { headers: { Authorization: "Bearer " + this.token } })
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: `Review ${isEdit ? "updated" : "submitted"}.` });
            this.$emit("saved");
          } else {
            this.$q.notify({ type: "negative", message: res.data.message || "Failed to save review." });
          }
        })
        .catch(() => {
          this.$q.notify({ type: "negative", message: "Error communicating with server." });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    acknowledge() {
      this.saving = true;
      axios
        .post(
          this.apiUrl + "/performance/reviews/update",
          { id: this.form.id, employee_comments: this.form.employee_comments, status: "acknowledged" },
          { headers: { Authorization: "Bearer " + this.token } }
        )
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({ type: "positive", message: "Review acknowledged." });
            this.$emit("saved");
          } else {
            this.$q.notify({ type: "negative", message: res.data.message || "Failed." });
          }
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>

<style scoped>
.rounded-borders {
  border-radius: 8px;
}
</style>
